/**
 * First run: two friendly questions (four with «Путь пешки» 2.0: the age band and «Знаешь, как ходят фигуры?»,
 * docs/PATH-V2.md §5.3), then Гамбитик waves and greets.
 * State logic is in onboarding.ts; this file is the view + the side effects (save, speak).
 *
 * With the path's questions (four screens) every screen says its own question at once: a new screen cuts the line of
 * the screen before (a child answers while Гамбитик is still asking), so the question never lags a screen behind; the
 * greeting starts on the welcome card. Without them (the path off) the old two questions play out as before.
 */
import { useEffect, useReducer, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { buildGreeting } from '@gambit/core';
import { STAGE_POOLS } from '@gambit/content';
import type { AgeBand, StudentProfile } from '@gambit/shared';
import { coach, useCoachStore } from '../coach/index.ts';
import { BigChoice, Button, Icon } from '../ui/index.ts';
import type { SaveStudentResult } from './appStore.ts';
import { scheduleGreeting, shellCoachEvent } from './greeting.ts';
import { ONBOARDING_ADDRESS_PHRASE, ONBOARDING_NAME_PHRASE } from './shellPhrases.ts';
import {
  NICKNAME_MAX_LENGTH,
  initialOnboardingState,
  nicknameProblemRu,
  onboardingPathResult,
  onboardingReducer,
  onboardingSteps,
  readSignupDraft,
  signupDraftOf,
  signupStorage,
  writeSignupDraft,
} from './onboarding.ts';
import type { Address, OnboardingStep } from './onboarding.ts';
import styles from './Onboarding.module.css';
import shell from './shell.module.css';

export interface OnboardingProps {
  profile: StudentProfile | null;
  saveStudent: (patch: { nickname: string; address: Address }) => Promise<SaveStudentResult>;
  /** the child pressed «Поехали!» (with the path's answers when it asked them) */
  onDone: (path?: { ageBand: AgeBand; knowsMoves: boolean }) => void;
  /** «Путь пешки» 2.0 is on: ask the age band and «Знаешь, как ходят фигуры?» too (§5.3) */
  path?: boolean;
  /** the path has stage 0's chapters to play (the shell's `hasStageZero`; default true) */
  stageZero?: boolean;
}

/** The path's line for a sign-up question — the path's voice code comes only with the path (fixer r1: the first load). */
function sayPathLine(pool: string): void {
  void import('../features/path/pathSay.ts').then((m) => m.sayPathBeat(pool));
}

const AGE_CHOICES: readonly { band: AgeBand; title: string; icon: string; accent: 'green' | 'blue' | 'coral' }[] = [
  { band: '5-6', title: '5–6', icon: '🧒', accent: 'green' },
  { band: '7-8', title: '7–8', icon: '🧑', accent: 'blue' },
  { band: '9+', title: '9+', icon: '🧑‍🎓', accent: 'coral' },
];

const QUESTION_NAME = shellCoachEvent(ONBOARDING_NAME_PHRASE);
const QUESTION_ADDRESS = shellCoachEvent(ONBOARDING_ADDRESS_PHRASE);

export function Onboarding({ profile, saveStudent, onDone, path = false, stageZero = true }: OnboardingProps) {
  // with the path a sign-up left after the address (a reload) resumes on its step (onboarding.ts `SignupDraft`)
  const [state, dispatch] = useReducer(onboardingReducer, profile, (p) => initialOnboardingState(p, { path, draft: path ? readSignupDraft(signupStorage()) : null }));
  const steps = onboardingSteps(state);
  // resumed past the address: the profile on the server is the saved one (the welcome greets by it)
  const saved = useRef<StudentProfile | null>(state.nickname !== null ? profile : null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cancelGreeting = useRef<(() => void) | null>(null);
  const spokenStep = useRef<OnboardingStep | null>(null);

  // Гамбитик asks the questions aloud (held by the coach until the first click unlocks sound); with the path's four
  // screens a new screen first cuts what the screen before was still saying (the welcome effect below runs after this
  // one: its greeting is not cut). The path off: nothing is cut — the old sign-up exactly.
  useEffect(() => {
    if (path && spokenStep.current !== null && spokenStep.current !== state.step) coach.stopSpeaking({ clearBubble: true });
    spokenStep.current = state.step;
    if (state.step === 'name') void coach.say(QUESTION_NAME);
    if (state.step === 'address') void coach.say(QUESTION_ADDRESS);
    if (state.step === 'age') sayPathLine(STAGE_POOLS.signupAge);
    if (state.step === 'moves') sayPathLine(STAGE_POOLS.signupMoves);
    // `path` is fixed for the life of the screen (the reducer's steps are made from it once)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step]);

  // the welcome: Гамбитик greets the child by the saved profile (after the path's questions when they are asked)
  useEffect(() => {
    if (state.step !== 'welcome' || saved.current === null) return;
    const greeted = saved.current;
    cancelGreeting.current?.();
    cancelGreeting.current = scheduleGreeting({
      store: useCoachStore,
      say: (coachEvent) => coach.say(coachEvent),
      buildEvent: () => buildGreeting({ profile: greeted, hour: new Date().getHours() }),
    });
  }, [state.step]);

  useEffect(() => {
    if (state.step === 'name') inputRef.current?.focus();
  }, [state.step]);

  // the path's answers so far live in this browser until «Поехали!» (App removes them there): a reload resumes here
  useEffect(() => {
    const draft = signupDraftOf(state);
    if (draft) writeSignupDraft(signupStorage(), draft);
  }, [state]);

  useEffect(() => () => cancelGreeting.current?.(), []);

  const submitName = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    dispatch({ type: 'submitNickname' });
  };

  const chooseAddress = (address: Address): void => {
    if (saving || state.nickname === null) return;
    const nickname = state.nickname;
    setSaving(true);
    void saveStudent({ nickname, address })
      .then((result) => {
        if (result.status === 'rejected') {
          dispatch({ type: 'nicknameRejected' });
          return;
        }
        saved.current = result.profile;
        dispatch({ type: 'chooseAddress', address });
      })
      .finally(() => setSaving(false));
  };

  const problemId = 'onboarding-name-problem';

  return (
    <div className={styles.page}>
      <ol className={styles.dots} aria-label="Шаги знакомства">
        {steps.map((step, index) => (
          <li key={step} data-on={step === state.step ? 'true' : 'false'} data-done={steps.indexOf(state.step) > index ? 'true' : 'false'}>
            <span className={styles.srOnly}>
              Шаг {index + 1} из {steps.length}
            </span>
          </li>
        ))}
      </ol>

      {state.step === 'name' ? (
        <form className={styles.card} onSubmit={submitName} noValidate>
          <h1 className={styles.question}>Как тебя зовут?</h1>
          <input
            ref={inputRef}
            className={shell.field}
            data-size="hero"
            type="text"
            name="nickname"
            autoComplete="off"
            autoCapitalize="words"
            spellCheck={false}
            enterKeyHint="next"
            maxLength={NICKNAME_MAX_LENGTH + 8}
            placeholder="Например, Тигр"
            aria-label="Твоё имя или прозвище"
            aria-invalid={state.problem !== null}
            aria-describedby={state.problem !== null ? problemId : undefined}
            value={state.nicknameInput}
            onChange={(event) => dispatch({ type: 'typeNickname', value: event.target.value })}
            onKeyDown={(event) => {
              // explicit, so «Enter» works the same in every browser and embedded web view
              if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                event.preventDefault();
                dispatch({ type: 'submitNickname' });
              }
            }}
          />
          <p id={problemId} className={styles.problem} role="status">
            {state.problem !== null ? nicknameProblemRu(state.problem) : ''}
          </p>
          <Button type="submit" size="xl" iconAfter={<Icon name="forward" />}>
            Дальше
          </Button>
          <p className={styles.parentNote}>Для родителей: подойдёт имя или прозвище. Фамилию и другие личные данные указывать не нужно.</p>
        </form>
      ) : null}

      {state.step === 'address' ? (
        <section className={styles.card} aria-busy={saving}>
          <h1 className={styles.question}>Ты мальчик или девочка?</h1>
          <div className={styles.pair}>
            <BigChoice accent="blue" icon="👦" title="Мальчик" disabled={saving} onClick={() => chooseAddress('m')} />
            <BigChoice accent="coral" icon="👧" title="Девочка" disabled={saving} onClick={() => chooseAddress('f')} />
          </div>
          <p className={styles.parentNote}>
            Для родителей: это нужно только для русской грамматики — чтобы Гамбитик говорил «ты нашёл» или «ты нашла». Изменить можно в настройках.
          </p>
          <Button variant="ghost" icon={<Icon name="back" />} disabled={saving} onClick={() => dispatch({ type: 'back' })}>
            Назад
          </Button>
        </section>
      ) : null}

      {state.step === 'age' ? (
        <section className={styles.card}>
          <h1 className={styles.question}>Сколько тебе лет?</h1>
          <div className={styles.trio}>
            {AGE_CHOICES.map((c) => (
              <BigChoice key={c.band} layout="column" accent={c.accent} icon={c.icon} title={c.title} onClick={() => dispatch({ type: 'chooseAge', ageBand: c.band })} />
            ))}
          </div>
          <p className={styles.parentNote}>
            Для родителей: храним только полосу возраста, без даты рождения. Пока приложение её только запоминает. Изменить можно в «Для взрослых» на карте пути.
          </p>
          <Button variant="ghost" icon={<Icon name="back" />} onClick={() => dispatch({ type: 'back' })}>
            Назад
          </Button>
        </section>
      ) : null}

      {state.step === 'moves' ? (
        <section className={styles.card}>
          <h1 className={styles.question}>Знаешь, как ходят фигуры?</h1>
          <div className={styles.pair}>
            <BigChoice accent="green" icon="♞" title="Да!" onClick={() => dispatch({ type: 'chooseMoves', knowsMoves: true })} />
            <BigChoice accent="sunny" icon="🌱" title="Пока нет" onClick={() => dispatch({ type: 'chooseMoves', knowsMoves: false })} />
          </div>
          <p className={styles.parentNote}>
            {stageZero
              ? 'Для родителей: «Да» — сначала двенадцать загадок, они подберут начало пути. «Пока нет» — начнём со ступени 0: как ходят фигуры.'
              : 'Для родителей: «Да» — сначала двенадцать загадок, они подберут начало пути. «Пока нет» — начнём с первой ступени: уроки «как ходят фигуры» ещё готовятся.'}
          </p>
          <Button variant="ghost" icon={<Icon name="back" />} onClick={() => dispatch({ type: 'back' })}>
            Назад
          </Button>
        </section>
      ) : null}

      {state.step === 'welcome' ? (
        <section className={styles.card}>
          <div className={styles.hello} aria-hidden="true">
            ♞
          </div>
          <h1 className={styles.question}>Приятно познакомиться, {state.nickname}!</h1>
          <p className={styles.lead}>Я Гамбитик. Будем играть, решать задачи и думать вместе. Если что — я всегда в углу экрана.</p>
          <Button size="xl" variant="accent" icon={<Icon name="play" />} onClick={() => onDone(onboardingPathResult(state) ?? undefined)}>
            Поехали!
          </Button>
        </section>
      ) : null}
    </div>
  );
}
